import type { ApprovalStatus, Notification, NotificationType } from '@shared/types';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { apiUrl } from '@/config/api'; // Assuming you have this config

const NOTIFICATION_TYPE_LABELS: Record<NotificationType, string> = {
  NEW_ORGANIZATION: '組織新設',
  STOP_RECRUITMENT: '募集停止',
  ABOLISH_ORGANIZATION: '組織廃止',
};

const STATUS_STYLES: Record<ApprovalStatus, { bg: string; text: string }> = {
  DRAFT: { bg: 'bg-gray-100', text: 'text-gray-700' },
  PENDING: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  APPROVED: { bg: 'bg-green-100', text: 'text-green-800' },
  REJECTED: { bg: 'bg-red-100', text: 'text-red-800' },
};

const STATUS_LABELS: Record<ApprovalStatus, string> = {
  DRAFT: '下書き',
  PENDING: '承認待ち',
  APPROVED: '承認済み',
  REJECTED: '却下',
};

export default function NotificationList() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [filterStatus, setFilterStatus] = useState<ApprovalStatus | 'ALL'>('ALL');

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await fetch(apiUrl('/api/notifications'));
        if (response.ok) {
          const data = (await response.json()) as Notification[];
          setNotifications(data);
        }
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      }
    };
    fetchNotifications();
  }, []);

  const handleApprove = async (id: string) => {
    // This is a simplified example. In a real app, you'd get the user ID from context.
    const approvedBy = 'current_user';
    try {
      const response = await fetch(apiUrl(`/api/notifications/${id}/approve`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approvedBy }),
      });
      if (response.ok) {
        // Refresh the list
        const updatedNotifications = notifications.map((n) =>
          n.id === id ? { ...n, status: 'APPROVED' as const } : n,
        );
        setNotifications(updatedNotifications);
        alert('承認しました。');
      } else {
        alert('承認に失敗しました。');
      }
    } catch (error) {
      console.error('Failed to approve notification:', error);
    }
  };

  const handleReject = (id: string) => {
    // Reject logic will be implemented later
    alert(`却下しました（モック）: ${id}`);
  };

  const filteredNotifications =
    filterStatus === 'ALL' ? notifications : notifications.filter((n) => n.status === filterStatus);

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">届け出一覧</h1>
        <Link to="/site/notifications/new">
          <Button>新規届け出</Button>
        </Link>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6">
        {(['ALL', 'PENDING', 'APPROVED', 'REJECTED'] as (ApprovalStatus | 'ALL')[]).map((status) => (
          <button
            key={status}
            type="button"
            onClick={() => setFilterStatus(status)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filterStatus === status ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {status === 'ALL' ? 'すべて' : STATUS_LABELS[status]}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white border rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">種別</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">対象</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">申請者</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">ステータス</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">反映状態</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">操作</th>
            </tr>
          </thead>
          <tbody>
            {filteredNotifications.map((notification) => (
              <tr key={notification.id} className="border-b hover:bg-gray-50">
                <td className="px-4 py-3">{NOTIFICATION_TYPE_LABELS[notification.type]}</td>
                <td className="px-4 py-3">
                  {notification.type === 'NEW_ORGANIZATION'
                    ? notification.payload.nameFormal
                    : notification.payload.targetName}
                </td>
                <td className="px-4 py-3 text-sm">{notification.requestedBy}</td>
                <td className="px-4 py-3">
                  <span
                    className={`text-xs px-2 py-1 rounded ${
                      STATUS_STYLES[notification.status].bg
                    } ${STATUS_STYLES[notification.status].text}`}
                  >
                    {STATUS_LABELS[notification.status]}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm">
                  {notification.isSubmitted && <span className="text-green-600">実施済み</span>}
                  {notification.forceApply && <span className="text-orange-600">強制適用</span>}
                  {!notification.isSubmitted && !notification.forceApply && (
                    <span className="text-gray-500">未対応</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {notification.status === 'PENDING' && (
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleApprove(notification.id)}>
                        承認
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleReject(notification.id)}>
                        却下
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
